// MetaspaceToolbar.tsx
import React, { useMemo, useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { useTranslation } from 'localization';

import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Tooltip } from 'primereact/tooltip';

import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import SphereChatPanel from '../../molecules/SphereChatPanel/SphereChatPanel';
import { type ToolbarItem, resolveToolbarItemAlias, resolveToolbarItemAliasList } from './toolbarItems';
import { NotificationsMode, TasksMode, StatusSummaryMode } from '../../molecules/ArmillaryActions/modes';
import StreamPanelOrganism from '../StreamPanel/StreamPanelOrganism';
import TrajectoryPanelOrganism, { type TrajectoryFocusRequest } from '../TrajectoryPanel/TrajectoryPanelOrganism';
import { useMetaspaceEvents } from '../../../hooks/useMetaspaceEvents';
import { useToolbarBadges } from '../../../hooks/useToolbarBadges';
import { useToolbarState } from '../../../context/ToolbarStateContext';
import { useToolbarContextOrNull } from '../../../context/ToolbarContext';

import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './metaspacetoolbar.scss';

export type { ToolbarItem } from './toolbarItems';
export { resolveToolbarItemAlias } from './toolbarItems';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface MetaspaceToolbarProps {
  sphereId?: string;
  isOpen?: boolean;
  activeItem?: ToolbarItem;
  onOpenChange?: (isOpen: boolean) => void;
  chatSphereId?: string; // Specific sphereId for chat context
  // Props for toolbar integration
  defaultOpen?: boolean; // Initial open state (uncontrolled)
  defaultActiveItem?: ToolbarItem; // Initial active item (uncontrolled)
  items?: ToolbarItem[]; // Which toolbar items to display (defaults to all)
  disabledItems?: string[]; // Which toolbar items to hide (all others shown)
  activeSphereId?: string; // Active sphere ID for mode components
  additionalSphereIds?: string[]; // Orbital sphere IDs for mode components
  timelineSphereId?: string; // Override sphere ID for timeline panel
  chatboxContextMode?: 'sphere_only' | 'sphere_and_metaspace';
  /** Callback to open the sphere editor for a workflow task */
  onEditSphere?: (taskId: string, task: import('../../molecules/TaskList/TaskListMolecule.types').Task) => void;
}

const MetaspaceToolbar: React.FC<MetaspaceToolbarProps> = ({
  sphereId: sphereIdProp,
  isOpen: controlledIsOpen,
  activeItem: controlledActiveItem,
  onOpenChange,
  chatSphereId,
  defaultOpen = false,
  defaultActiveItem = 'chatbox',
  items,
  disabledItems,
  activeSphereId,
  additionalSphereIds,
  timelineSphereId,
  onEditSphere,
  chatboxContextMode,
}) => {
  // --- Localization and toolbar context ---
  const { t } = useTranslation();
  const toolbarContext = useToolbarContextOrNull();
  const navigate = useNavigate();

  // --- Responsive panel sizing ---
  // Calculate panel dimensions based on viewport size
  const calculateDimensions = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    const isTablet = viewportWidth < 1024;

    const minHeight = isMobile ? 300 : 400;
    const maxHeight = viewportHeight * (isMobile ? 0.7 : 0.8);

    // Stream/Trajectory's fixed-width chrome (band legend, cadence row minimums) needs 340px to
    // stay readable — below that the panel switches to an overlay sheet instead of squeezing.
    const minWidth = isMobile ? 280 : 340;
    const isOverlay = viewportWidth < 1200;

    let defaultWidth;
    if (isMobile) {
      defaultWidth = 300;
    } else if (isTablet) {
      defaultWidth = 350;
    } else if (viewportWidth >= 2560) {
      defaultWidth = 600; // 4K+ screens
    } else if (viewportWidth >= 1920) {
      defaultWidth = 500; // Full HD+ screens
    } else {
      defaultWidth = 450; // Standard desktop (1024px-1919px)
    }

    return {
      width: defaultWidth,
      minHeight,
      minWidth,
      maxHeight,
      isOverlay,
    };
  }, []);

  const [panelDimensions, setPanelDimensions] = useState(calculateDimensions);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      // Debounce 150ms to avoid excessive recalculations
      timeoutId = setTimeout(() => {
        setPanelDimensions(calculateDimensions());
      }, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateDimensions]);

  // --- sphere & metaspace -> contextKey ---
  const { sphereId: sphereIdFromUrl, metaspaceId } = useParams<{ sphereId?: string; metaspaceId?: string }>();
  const sphereIdFromStore = useSelector((s: RootState) => s.nodes?.currentSphereId || s.nodes?.selectedSphereId);
  const metaspaces = useSelector(selectAllMetaspaces);
  const sphereId = sphereIdProp ?? sphereIdFromUrl ?? sphereIdFromStore ?? 'default';

  // --- UI state ---
  // Use controlled value if provided, otherwise use default prop for initial state.
  // Resolve legacy tool ids (`activity`/`timeline`) at the boundary — every prop
  // below may come from a stored metaspace setting that predates the migration.
  const [isPanelOpen, setIsPanelOpen] = useState(controlledIsOpen ?? defaultOpen);
  const [internalActiveItem, setInternalActiveItem] = useState<ToolbarItem>(
    resolveToolbarItemAlias(controlledActiveItem ?? defaultActiveItem)
  );
  const [taskStatusFilter, setTaskStatusFilter] = useState<string | null>(null);
  const [anchorMessageId, setAnchorMessageId] = useState<string | null>(null);

  // --- Toolbar state context integration ---
  const { setIsExpanded } = useToolbarState();

  // Get the current active item (controlled or internal)
  const activeItem = controlledActiveItem ? resolveToolbarItemAlias(controlledActiveItem) : internalActiveItem;

  // Report active panel to ToolbarContext for lazy data loading
  useEffect(() => {
    toolbarContext?.setActivePanel(isPanelOpen ? activeItem : null);
  }, [activeItem, isPanelOpen, toolbarContext]);

  // Helper functions to reduce cognitive complexity
  const handleChatTriggeredEvent = useCallback(
    (payload: any) => {
      // Set the active chat sphere ID from the event
      if (payload.sphereId) {
        setActiveChatSphereId(payload.sphereId);
      }

      // Set the active metaspace ID from the event
      if (payload.metaspaceId) {
        setActiveMetaspaceId(payload.metaspaceId);
      }

      // Open the toolbar and set active item to chat when chat is triggered from Atlas
      if (controlledIsOpen === undefined) {
        setIsPanelOpen(true);
      }
      if (controlledActiveItem === undefined) {
        setInternalActiveItem('chatbox');
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleToolbarExpandEvent = useCallback(
    (payload: any) => {
      // Handle toolbar expansion events
      if (payload.activeItem && controlledActiveItem === undefined) {
        setInternalActiveItem(resolveToolbarItemAlias(payload.activeItem));
      }
      // Scope the status tool to a specific sphere when the opener asks for one
      // (Atlas row status-icon click). Only the status panel honors this; other
      // panels keep following the centered sphere.
      if (payload.activeItem === 'status') {
        setActiveStatusSphereId(payload.sphereId || undefined);
      }
      if (controlledIsOpen === undefined) {
        setIsPanelOpen(true);
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleThreadContextLoadEvent = useCallback((payload: any) => {
    // If this is for chat context, update the active sphere ID
    if (payload.sphereId) {
      setActiveChatSphereId(payload.sphereId);
    }

    // If this is for chat context, update the active metaspace ID
    if (payload.metaspaceId) {
      setActiveMetaspaceId(payload.metaspaceId);
    }
  }, []);

  const handleTaskFilterEvent = useCallback(
    (payload: any) => {
      setTaskStatusFilter(payload.status ?? null);

      if (payload.status != null) {
        // Open toolbar and switch to tasks panel
        if (controlledIsOpen === undefined) {
          setIsPanelOpen(true);
        }
        if (controlledActiveItem === undefined) {
          setInternalActiveItem('tasks');
        }
        onOpenChange?.(true);
      }
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleTimelineTriggeredEvent = useCallback(
    (payload: any) => {
      if (payload.timelineSphereId) {
        setActiveTimelineSphereId(payload.timelineSphereId);
      }

      if (controlledIsOpen === undefined) {
        setIsPanelOpen(true);
      }
      if (controlledActiveItem === undefined) {
        setInternalActiveItem('trajectory');
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange]
  );

  const handleConversationMessageClickedEvent = useCallback(
    (payload: any) => {
      if (disabledItems?.includes('chatbox')) {
        return;
      }

      if (payload.sphereId) {
        setActiveChatSphereId(payload.sphereId);
      }

      setAnchorMessageId(payload.messageId ?? null);

      if (controlledIsOpen === undefined) {
        setIsPanelOpen(true);
      }
      if (controlledActiveItem === undefined) {
        setInternalActiveItem('chatbox');
      }
      onOpenChange?.(true);
    },
    [controlledIsOpen, controlledActiveItem, onOpenChange, disabledItems]
  );

  // Memoize the event handler to prevent infinite loops
  const handleMetaspaceEvent = useCallback(
    (event: { type: string; payload: any }) => {
      switch (event.type) {
        case 'CHAT_TRIGGERED':
          handleChatTriggeredEvent(event.payload);
          break;
        case 'TOOLBAR_EXPAND':
          handleToolbarExpandEvent(event.payload);
          break;
        case 'THREAD_CONTEXT_LOAD':
          handleThreadContextLoadEvent(event.payload);
          break;
        case 'TASK_FILTER_TRIGGERED':
          handleTaskFilterEvent(event.payload);
          break;
        case 'TIMELINE_TRIGGERED':
          handleTimelineTriggeredEvent(event.payload);
          break;
        case 'CONVERSATION_MESSAGE_CLICKED':
          handleConversationMessageClickedEvent(event.payload);
          break;
        default:
          break;
      }
    },
    [handleChatTriggeredEvent, handleToolbarExpandEvent, handleThreadContextLoadEvent, handleTaskFilterEvent, handleTimelineTriggeredEvent, handleConversationMessageClickedEvent]
  );

  // Memoize event types array to prevent re-subscription
  const eventTypes = useMemo(() => ['CHAT_TRIGGERED', 'TOOLBAR_EXPAND', 'THREAD_CONTEXT_LOAD', 'TASK_FILTER_TRIGGERED', 'TIMELINE_TRIGGERED', 'CONVERSATION_MESSAGE_CLICKED'], []);

  // Subscribe to metaspace events
  useMetaspaceEvents({
    subscriberId: 'metaspace-toolbar',
    eventTypes,
    onEvent: handleMetaspaceEvent,
  });

  // State to track the active chat context from events
  const [activeChatSphereId, setActiveChatSphereId] = useState<string | undefined>(chatSphereId);
  const [activeMetaspaceId, setActiveMetaspaceId] = useState<string | undefined>(metaspaceId);
  const [activeTimelineSphereId, setActiveTimelineSphereId] = useState<string | undefined>(timelineSphereId);
  // Per-open status sphere, set from TOOLBAR_EXPAND.payload.sphereId when the
  // status tool is opened for a specific sphere (e.g. an Atlas row's status
  // icon). Scopes the status panel to that sphere without re-centering the
  // whole toolbar (which trackedSphereId/SPHERE_CENTERED would do).
  const [activeStatusSphereId, setActiveStatusSphereId] = useState<string | undefined>(undefined);

  // Use chatSphereId for chat context, fallback to regular sphereId for other items
  // Include metaspaceId in the context key format: sphere:<id>|metaspace:<uuid>
  const contextKey = useMemo(() => {
    const isChatboxActive = activeItem === 'chatbox';
    const hasChatSphereId = activeChatSphereId || chatSphereId;
    const currentSphereId = isChatboxActive && hasChatSphereId ? activeChatSphereId || chatSphereId : (toolbarContext?.trackedSphereId || sphereId);

    // If chatbox contextMode is sphere_only, skip metaspace segment entirely
    if (chatboxContextMode === 'sphere_only') {
      return `sphere:${currentSphereId}`;
    }

    const currentMetaspaceId = activeMetaspaceId || metaspaceId;

    if (currentMetaspaceId) {
      // Resolve system_name to UUID if needed
      let resolvedMetaspaceId = currentMetaspaceId;

      if (!UUID_REGEX.test(currentMetaspaceId) && metaspaces?.length > 0) {
        const found = metaspaces.find((m: any) => m.system_name === currentMetaspaceId);
        if (found?.metaspace_id) {
          resolvedMetaspaceId = found.metaspace_id;
        }
      }

      return `sphere:${currentSphereId}|metaspace:${resolvedMetaspaceId}`;
    }
    return `sphere:${currentSphereId}`;
  }, [activeItem, activeChatSphereId, chatSphereId, sphereId, activeMetaspaceId, metaspaceId, chatboxContextMode, metaspaces, toolbarContext?.trackedSphereId]);

  // Sync with controlled props
  useEffect(() => {
    if (controlledIsOpen !== undefined) {
      setIsPanelOpen(controlledIsOpen);
    }
  }, [controlledIsOpen]);

  useEffect(() => {
    if (controlledActiveItem !== undefined) {
      setInternalActiveItem(resolveToolbarItemAlias(controlledActiveItem));
    }
  }, [controlledActiveItem]);

  // Sync activeMetaspaceId with metaspaceId prop
  useEffect(() => {
    if (metaspaceId !== undefined) {
      setActiveMetaspaceId(metaspaceId);
    }
  }, [metaspaceId]);

  // Sync activeTimelineSphereId with timelineSphereId prop
  useEffect(() => {
    if (timelineSphereId !== undefined) {
      setActiveTimelineSphereId(timelineSphereId);
    }
  }, [timelineSphereId]);

  // Sync toolbar expansion state with context
  useEffect(() => {
    setIsExpanded(isPanelOpen);
  }, [isPanelOpen, setIsExpanded]);

  // --- Refs ---
  const shellRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const activeElRef = useRef<HTMLDivElement | null>(null);
  const [connectorTop, setConnectorTop] = useState<number | null>(null);

  // Get badge counts and severity from custom hook
  const { unreadCount, taskCount, taskBadgeSeverity, activityBadgeCount, activityBadgeSeverity, overdueCheckInCount, statusBadgeSeverity } = useToolbarBadges();

  // Define all available toolbar items with localized labels. Stream/Trajectory
  // render via GlyphAtom (Iconify `lucide:*`) rather than PrimeIcons — see
  // types/glyph.d.ts: PrimeIcon app chrome stays `pi-*`, the glyph model is
  // reserved for the unified icon system this pair adopts first.
  const allToolbarItems = useMemo(() => [
    {
      id: 'stream' as ToolbarItem,
      glyph: 'lucide:align-left',
      count: activityBadgeCount,
      title: t('toolbar.stream'),
      severity: activityBadgeSeverity,
    },
    {
      id: 'notifications' as ToolbarItem,
      icon: 'pi pi-bell',
      count: unreadCount,
      title: t('toolbar.notifications'),
      severity: unreadCount > 0 ? 'danger' as const : 'secondary' as const,
    },
    {
      id: 'tasks' as ToolbarItem,
      icon: 'pi pi-check-square',
      count: taskCount,
      title: t('toolbar.tasks'),
      severity: taskBadgeSeverity,
    },
    {
      id: 'status' as ToolbarItem,
      icon: 'pi pi-chart-bar',
      count: overdueCheckInCount,
      title: t('toolbar.status'),
      severity: statusBadgeSeverity,
    },
    {
      id: 'chatbox' as ToolbarItem,
      icon: 'chat-bubble-icon',
      count: 0,
      title: t('toolbar.chat'),
      severity: 'secondary' as const,
    },
    {
      id: 'trajectory' as ToolbarItem,
      glyph: 'lucide:trending-up',
      count: 0,
      title: t('toolbar.trajectory'),
      severity: 'secondary' as const,
    },
  ], [t, unreadCount, taskCount, taskBadgeSeverity, activityBadgeCount, activityBadgeSeverity, overdueCheckInCount, statusBadgeSeverity]);

  // Filter items: enabledToolbarItems (allowlist) > disabledToolbarItems (denylist) > show all.
  // `items`/`disabledItems` may carry legacy tool ids from an unmigrated metaspace setting.
  const toolbarItems = useMemo(() => {
    if (items && items.length > 0) {
      const resolvedItems = resolveToolbarItemAliasList(items);
      return allToolbarItems.filter(item => resolvedItems.includes(item.id));
    }
    if (disabledItems && disabledItems.length > 0) {
      const resolvedDisabledItems = resolveToolbarItemAliasList(disabledItems);
      return allToolbarItems.filter(item => !resolvedDisabledItems.includes(item.id));
    }
    return allToolbarItems;
  }, [allToolbarItems, items, disabledItems]);

  const handleItemClick = useCallback((itemId: string) => {
    const newIsOpen = !(activeItem === itemId && isPanelOpen);
    const newActiveItem = itemId as ToolbarItem;

    // Clear task filter when navigating away from tasks
    if (newActiveItem !== 'tasks') {
      setTaskStatusFilter(null);
    }

    // Opening the status tool from the rail (not a row click) should follow the
    // centered sphere — drop any per-row override left by an Atlas status click.
    if (newActiveItem === 'status') {
      setActiveStatusSphereId(undefined);
    }

    if (controlledIsOpen === undefined) {
      setIsPanelOpen(newIsOpen);
    }
    if (controlledActiveItem === undefined) {
      setInternalActiveItem(newActiveItem);
    }

    onOpenChange?.(newIsOpen);
  }, [activeItem, isPanelOpen, controlledIsOpen, controlledActiveItem, onOpenChange]);

  const handleBackClick = useCallback(() => {
    const newIsOpen = !isPanelOpen;

    if (controlledIsOpen === undefined) {
      setIsPanelOpen(newIsOpen);
    }

    onOpenChange?.(newIsOpen);
  }, [isPanelOpen, controlledIsOpen, onOpenChange]);

  // Calculate connector position based on active rail item
  const updateConnector = useCallback(() => {
    if (!railRef.current || !activeElRef.current) return;
    const railBox = railRef.current.getBoundingClientRect();
    const activeBox = activeElRef.current.getBoundingClientRect();

    // Position relative to the rail container
    const relativeTop = activeBox.top - railBox.top + activeBox.height / 2;
    setConnectorTop(relativeTop);
  }, []);

  useLayoutEffect(updateConnector, [activeItem, isPanelOpen, updateConnector]);

  // Force update on mount after refs are set (fixes initial render positioning)
  useEffect(() => {
    // Small delay to ensure refs are fully set after first render
    const timer = setTimeout(() => {
      updateConnector();
    }, 0);
    return () => clearTimeout(timer);
  }, [updateConnector]);

  useEffect(() => {
    const onResize = () => updateConnector();
    window.addEventListener('resize', onResize);
    const onScroll = () => updateConnector();
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [updateConnector]);

  // Determine the sphere ID to pass to mode components
  // Prefer trackedSphereId (updated by SPHERE_CENTERED events from armillary),
  // then activeSphereId prop, fallback to sphereId from URL/store
  const modeActiveSphereId = toolbarContext?.trackedSphereId || activeSphereId || sphereId;

  const handleClearFilter = useCallback(() => setTaskStatusFilter(null), []);
  const handleAnchorComplete = useCallback(() => setAnchorMessageId(null), []);

  const currentTimelineSphereId = activeTimelineSphereId || timelineSphereId || toolbarContext?.trackedSphereId || sphereId;

  // `/sphere/:sphereId/timeline` (singular) — the plural `/spheres/...` this
  // previously navigated to has no matching route and 404s.
  const handleViewAllClick = useCallback(
    () => navigate(`/sphere/${currentTimelineSphereId}/timeline`),
    [navigate, currentTimelineSphereId]
  );

  const handleStreamOpenSphere = useCallback(
    () => navigate(`/sphere/${modeActiveSphereId}`),
    [navigate, modeActiveSphereId]
  );

  const handleStreamOpenComments = useCallback(() => handleItemClick('chatbox'), [handleItemClick]);

  // Cross-panel threading (spec follow-up, now wired): jump-to-track carries the aspect into a
  // focus request the Trajectory panel scrolls to; a band's "Scope Stream" carries its aspect into
  // the Stream panel's server-side scope filter (dismissable via the scope chip).
  const [trajectoryFocus, setTrajectoryFocus] = useState<TrajectoryFocusRequest | null>(null);
  const [streamScopeAspect, setStreamScopeAspect] = useState<string | null>(null);

  const handleStreamJumpToTrack = useCallback(
    (aspectSystemName: string) => {
      setTrajectoryFocus((prev) => ({ aspectSystemName, nonce: (prev?.nonce ?? 0) + 1 }));
      handleItemClick('trajectory');
    },
    [handleItemClick]
  );

  const handleTrajectoryScopeStream = useCallback(
    (scope: { aspectSystemName: string; from: string; to: string | null }) => {
      setStreamScopeAspect(scope.aspectSystemName);
      handleItemClick('stream');
    },
    [handleItemClick]
  );
  const handleStreamClearScope = useCallback(() => setStreamScopeAspect(null), []);

  // chatbox uses a different wrapper class; all other modes use toolbar-mode-panel
  const panelWrapperClass = activeItem === 'chatbox' ? 'h-full metaspace-chat-panel' : 'h-full toolbar-mode-panel';

  const panelContent = useMemo(() => {
    switch (activeItem) {
      case 'stream':
        return (
          <StreamPanelOrganism
            sphereId={modeActiveSphereId}
            contextKey={contextKey}
            onOpenSphere={handleStreamOpenSphere}
            onOpenComments={handleStreamOpenComments}
            onJumpToTrack={handleStreamJumpToTrack}
            scopeAspect={streamScopeAspect}
            onClearScope={handleStreamClearScope}
          />
        );
      case 'notifications':
        return <NotificationsMode activeSphereId={modeActiveSphereId} additionalSphereIds={additionalSphereIds} />;
      case 'tasks':
        return (
          <TasksMode
            activeSphereId={modeActiveSphereId}
            additionalSphereIds={additionalSphereIds}
            onEditSphere={onEditSphere}
            statusFilter={taskStatusFilter}
            onClearFilter={handleClearFilter}
          />
        );
      case 'status':
        // Prefer the per-open status sphere (from an Atlas row status click)
        // over the centered sphere, so the panel shows the row the user clicked.
        return <StatusSummaryMode activeSphereId={activeStatusSphereId || modeActiveSphereId} />;
      case 'trajectory':
        return (
          <TrajectoryPanelOrganism
            sphereId={modeActiveSphereId}
            focusAspect={trajectoryFocus}
            onOpenFullTimeline={handleViewAllClick}
            onScopeStream={handleTrajectoryScopeStream}
          />
        );
      case 'chatbox':
      default:
        return (
          <SphereChatPanel contextKey={contextKey} collapsible={false} scrollToBottomOnLoad={true} showThreadContext={true} anchorMessageId={anchorMessageId} onAnchorComplete={handleAnchorComplete} />
        );
    }
  }, [activeItem, modeActiveSphereId, activeStatusSphereId, additionalSphereIds, onEditSphere,
      taskStatusFilter, handleClearFilter, contextKey, anchorMessageId,
      handleAnchorComplete, handleViewAllClick,
      handleStreamOpenSphere, handleStreamOpenComments, handleStreamJumpToTrack, handleTrajectoryScopeStream, trajectoryFocus, streamScopeAspect, handleStreamClearScope]);

  return (
    <div className="metaspace-shell flex mr-2" ref={shellRef}>
      {/* Icon Rail */}
      <div
        className="metaspace-rail flex flex-column border-right-1 surface-border shadow-2 px-2"
        ref={railRef}
        style={{
          height: '100%',
          minHeight: `${panelDimensions.minHeight}px`,
          maxHeight: `${panelDimensions.maxHeight}px`,
        }}
      >
        {isPanelOpen && activeItem && connectorTop !== null && (
          <div className="panel-connector" style={{ transform: `translateY(calc(${connectorTop}px - 50%))` }} aria-hidden />
        )}
        <div className="p-3 border-bottom-1 surface-border px-1 mb-2">
          <Button
            icon={isPanelOpen ? 'metaspace-toolbar-collapse-icon' : 'metaspace-toolbar-expand-icon'}
            onClick={handleBackClick}
            className="p-button-text p-button-plain w-full toolbar-item-back"
            size="small"
          />
        </div>

        {toolbarItems.map((item) => {
          const isActive = activeItem === item.id;
          return (
            <div
              key={item.id}
              className={`rail-item ${isActive ? 'is-active' : ''}`}
              role="tab"
              aria-selected={isActive}
              ref={(el) => {
                if (isActive) activeElRef.current = el;
              }}
            >
              <Tooltip target={`.toolbar-item-${item.id}`} content={item.title} position="right" />
              {item.count > 0 && <Badge value={item.count} severity={isActive ? 'info' : 'secondary'} className="rail-badge" />}
              <Button className={`toolbar-item-${item.id} rail-btn p-button-text`} onClick={() => handleItemClick(item.id)}>
                {item.glyph ? (
                  <GlyphAtom glyph={item.glyph} className={isActive ? 'text-primary' : 'text-600'} />
                ) : (
                  <i className={`${item.icon} ${isActive ? 'text-primary' : 'text-600'}`} />
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Panel - animated slide in/out. Below 1200px it becomes an overlay sheet (`panel-overlay`)
          instead of pushing the layout — see `metaspacetoolbar.scss` § responsive. */}
      <div
        ref={panelRef}
        className={`metaspace-panel flex-1 flex ${isPanelOpen ? 'panel-open' : 'panel-closed'} ${panelDimensions.isOverlay ? 'panel-overlay' : ''}`}
        style={{
          height: '100%',
          width: isPanelOpen ? `${panelDimensions.width}px` : '0px',
          minHeight: `${panelDimensions.minHeight}px`,
          minWidth: isPanelOpen ? `${panelDimensions.minWidth}px` : '0px',
          maxHeight: `${panelDimensions.maxHeight}px`,
        }}
      >
        <div className="flex-1 p-4 px-0 pt-0 flex flex-column panel-inner">
          <div className="flex-1 panel-scroll-container">
            <div className={panelWrapperClass}>
              {panelContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaspaceToolbar;
